"use client";
import { SingleNewsPageQueryResult } from "@/sanity.types";
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import clsx from "clsx";
import Link from "next/link";
import { hardcodedContent } from "@/app/(site)/_lib/global-content";
import { urlFor } from "@/sanity/lib/image";
import Image from "next/image";
import { useSocialMediaData } from "@/app/(site)/_hooks/useSanityData";
import { getIconForSocialMedia } from "@/app/(site)/_lib/helpers";

type TableOfContentProps = {
	moduleData: NonNullable<SingleNewsPageQueryResult>;
};

export default function TableOfContent({ moduleData }: TableOfContentProps) {
	const { tableOfContents, authors, title, slug } = moduleData;
	const [activeSection, setActiveSection] = useState<number | null>(null);
	const [sectionElements, setSectionElements] = useState<HTMLElement[]>([]);
	const { socialMedia } = useSocialMediaData();

	// Get current URL for sharing
	const uri = typeof window !== "undefined" ? window.location.href + "/" + slug : "";
	const encodedUri = encodeURIComponent(uri);
	const encodedTitle = encodeURIComponent(title);

	useEffect(() => {
		const sections = Array.from(document.querySelectorAll("h3[id^='category-']")) as HTMLElement[];
		setSectionElements(sections);
	}, []);

	useEffect(() => {
		const handleScroll = () => {
			const scrollPosition = window.scrollY;
			const offset = 50;

			const currentSectionIndex = sectionElements.findIndex((section, index) => {
				const sectionTop = section.getBoundingClientRect().top + scrollPosition;
				const nextSectionTop = sectionElements[index + 1]?.getBoundingClientRect().top + scrollPosition || Infinity;

				return scrollPosition + offset >= sectionTop && scrollPosition < nextSectionTop - 100;
			});

			if (currentSectionIndex !== -1) {
				setActiveSection(currentSectionIndex);
			}
		};

		window.addEventListener("scroll", handleScroll);
		return () => window.removeEventListener("scroll", handleScroll);
	}, [sectionElements]);

	const handleTitleClick = (e: React.MouseEvent, section: HTMLElement, index: number) => {
		e.preventDefault();
		const sectionTop = section.getBoundingClientRect().top + window.scrollY;

		if (window.scrollY < sectionTop) {
			window.scrollTo({ top: sectionTop, behavior: "smooth" });
		} else {
			window.scrollTo({ top: sectionTop - 90, behavior: "smooth" });
		}

		setActiveSection(index);
	};

	const getShareUrl = (name: string) => {
		const lowerName = name.toLowerCase();
		if (lowerName.includes("facebook")) {
			return `https://www.facebook.com/sharer.php?u=${encodedUri}&title=${encodedTitle}`;
		}
		// //FIXME: chyba nie działa to ogolnie na instagramie
		if (lowerName.includes("instagram")) {
			return `https://www.instagram.com/shareArticle?mini=true&url=${encodedUri}&title=${encodedTitle}`;
		}
		if (lowerName.includes("linkedin")) {
			return `https://www.linkedin.com/shareArticle?mini=true&url=${encodedUri}&title=${encodedTitle}`;
		}
		if (lowerName.includes("twitter") || lowerName.includes("x")) {
			return `https://x.com/intent/tweet?text=${encodedTitle}&url=${encodedUri}`;
		}
		return "#";
	};

	return (
		<div className="bg-middle-light sticky top-30 my-10 w-full rounded-lg p-5 sm:my-0 sm:p-10">
			<div className="flex flex-col gap-y-5 sm:gap-y-10">
				{tableOfContents === "enabled" && (
					<>
						<ul className="flex flex-col gap-y-3">
							<p className="text-cap-m text-olive-green mb-2">{hardcodedContent.singleNewsPage.tableOfContents}</p>
							{sectionElements.map((section, index) => (
								<motion.li key={section.id} className="relative" transition={{ duration: 0.2 }}>
									<Link
										href={`#${section.id}`}
										className="text-body-m flex items-baseline gap-3 transition-colors duration-300"
										onClick={(e) => handleTitleClick(e, section, index)}
									>
										<motion.div
											className={clsx(
												"bg-olive-green h-2 w-2 rounded-full",
												index === activeSection && "animate-bounce",
											)}
											initial={{ scale: 1, opacity: 1 }}
											transition={{
												duration: 0.3,
												ease: "easeInOut",
											}}
										/>
										<motion.span
											className={clsx(
												"transition-colors duration-300",
												index === activeSection ? "text-olive-green" : "text-brown-middle hover:text-olive-green",
											)}
										>
											{section.textContent}
										</motion.span>
									</Link>
								</motion.li>
							))}
						</ul>
					</>
				)}
				{tableOfContents === "enabled" && <div className="bg-brown h-[0.063rem] w-full" />}

				<div className="">
					<p className="text-cap-m text-olive-green mb-5">
						{authors.length > 1
							? hardcodedContent.singleNewsPage.authors
							: hardcodedContent.singleNewsPage.authors.slice(0, -1)}
					</p>
					<div className="flex flex-col gap-y-3">
						{authors.map((author) => (
							<div key={author.authorName} className="flex items-center gap-x-2.5">
								<div className="relative h-10 w-10 shrink-0 overflow-hidden rounded-full">
									<Image
										src={urlFor(author.image).url()}
										alt={author.authorName}
										fill
										className="object-cover object-center"
									/>
								</div>
								<div className="flex flex-col gap-y-1">
									<p className="text-olive-green text-cap-m">{author.authorName}</p>
									<p className="text-brown-middle text-cap-m">{author.authorRole}</p>
								</div>
							</div>
						))}
					</div>
				</div>

				<div className="bg-brown h-[0.063rem] w-full" />

				<div className="">
					<p className="text-cap-m text-olive-green mb-5">{hardcodedContent.singleNewsPage.share}</p>
					<div className="flex items-center gap-x-2">
						{socialMedia.map((socialMediaItem, index) => (
							<a
								key={`${socialMediaItem.name.toLowerCase()}-${index}`}
								href={getShareUrl(socialMediaItem.name)}
								target="_blank"
								rel="noopener noreferrer"
								className="bg-brown-middle hover:bg-middle-light hover:border-olive-green group relative flex h-11.5 w-11.5 cursor-pointer items-center justify-center rounded-full transition-all duration-100 hover:border"
							>
								<span className="text-middle-light text-body-l group-hover:text-olive-green transition-colors duration-100">
									{getIconForSocialMedia(socialMediaItem.name)}
								</span>
							</a>
						))}
					</div>
				</div>
			</div>
		</div>
	);
}
